import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useCalibration } from '../../context/calibrationContext';
import { useSessionState } from '../../context/sessionStateContext';
import { SessionLayout, EndSessionButton, PanelTitle, PanelText, Stepper, colors, fonts, MonitorIcon, PhotoCameraIcon } from '../../ui';
import { LobbyView } from './LobbyView';
import { useSessionPeople, useConfirmExitSession } from './hooks';

// Patient "waiting" states (no test stimulus on screen):
//   P2  before / during calibration: "You are about to begin your eye examinations." + stepper +
//       Screen / Camera status cards, inside the standard panel + video strip layout.
//   P7  examinations: the two-tile video layout with [Exit Session] while the clinician sets up
//       the next test.
// Which one is shown comes from the shared session state step (falls back to the calibration
// flags when the state API is not available).

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 560px;
  margin: 24px auto 0;
  font-family: ${fonts.family};
`;

const StatusCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  .art {
    width: 100%;
    aspect-ratio: 1.75;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: ${({ ok }) => (ok ? colors.success[500] : '#A3A7A6')};
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
  }
  .label {
    font-size: 14px;
    color: ${colors.slate[700]};
  }
  .label b {
    font-weight: 700;
  }
`;

const P2Content = ({ screenCalibrated, cameraCalibrated, distanceCollected }) => {
  const current = distanceCollected ? 2 : screenCalibrated ? 1 : 0;
  return (
    <div style={{ margin: 'auto 0', paddingTop: 8 }}>
      {/* P2 shows the intro; P4 (screen calibrated) is the stepper and the status cards only. */}
      {!screenCalibrated && (
        <>
          <PanelTitle>You are about to begin your eye examinations.</PanelTitle>
          <PanelText>Please collaborate with your clinician to complete these steps before we can proceed.</PanelText>
        </>
      )}
      <div style={{ margin: '16px 0 8px' }}>
        <Stepper current={current} />
      </div>
      <StatusGrid>
        <StatusCard ok={screenCalibrated}>
          <div className='art'>
            <MonitorIcon size={48} strokeWidth={1.6} />
          </div>
          <span className='label'>
            <b>Screen:</b> {screenCalibrated ? 'Calibrated Successfully' : 'Not Calibrated'}
          </span>
        </StatusCard>
        <StatusCard ok={cameraCalibrated}>
          <div className='art'>
            <PhotoCameraIcon size={44} strokeWidth={1.6} />
          </div>
          <span className='label'>
            <b>Camera:</b> {cameraCalibrated ? 'Calibrated Successfully' : 'Not Calibrated'}
          </span>
        </StatusCard>
      </StatusGrid>
    </div>
  );
};

export const PatientWaiting = ({ onMount }) => {
  const people = useSessionPeople();
  const exitSession = useConfirmExitSession();
  const { state } = useSessionState();
  const calibration = useCalibration();
  const { screenCalibrated, cameraCalibrated } = calibration?.calibrationData || {};
  useEffect(() => {
    onMount?.();
  }, [onMount]);

  const step = state?.step;
  // Anything after calibration / distance (test picker, a running test, results, consultation).
  const examinations = Boolean(step) && !['calibration-selection', 'set-distance', 'welcomeScreen'].includes(step);

  if (examinations) {
    return (
      <LobbyView people={people} localFirst={false} left={<EndSessionButton onClick={exitSession}>Exit Session</EndSessionButton>} />
    );
  }
  return (
    <SessionLayout
      endAction={{ label: 'Exit Session', onClick: exitSession }}
      video={{ localLabel: people.localLabel, localName: people.localName, remoteLabel: people.remoteLabel, remoteName: people.remoteName, order: 'remote-first' }}
    >
      <P2Content
        screenCalibrated={Boolean(screenCalibrated || state?.calibration?.screen_calibrated)}
        cameraCalibrated={Boolean(cameraCalibrated || state?.calibration?.camera_calibrated)}
        distanceCollected={Boolean(state?.distance?.mm)}
      />
    </SessionLayout>
  );
};

export default PatientWaiting;
